import type { FeatureFlagService } from "../../services/feature-flags";
import type { VersionedStorageService } from "../../services/versioned-storage";
import { preflightStorageKey } from "./preflight";
import {
  buildQuestionBank,
  isAnswered,
  revealedWithoutAnswering,
  seededRandom,
  selectQuestions,
  type QuestionResponse,
} from "./questions";
import type { OrdersParseTree } from "./parser";

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

export interface QuestionsPhaseOptions {
  readonly fileHash: string;
  readonly flags: FeatureFlagService;
  /**
   * Ruling 84: questions started and finished, with times.
   *
   * Counts only. The ruling's last sentence is "It records no text of his
   * answers", and this phase holds the only text he writes, so what it
   * reports is how many questions there were and how many he had revealed —
   * never a question, never an answer, never his typing.
   */
  readonly onMoment?: (
    name: string,
    detail: Readonly<Record<string, number>>,
  ) => void;
  readonly seed?: number;
  readonly storage: VersionedStorageService;
  readonly tree: OrdersParseTree;
}

/**
 * The confirmatory questions phase. R1 Step 14.
 *
 * Runs after delivery and touches no part of it. The trainee types an answer,
 * reveals the parsed value, and marks himself. Nothing here scores or grades;
 * the only signal stored is which questions he revealed without answering.
 */
export function mountConfirmatoryQuestions(
  target: HTMLElement,
  options: QuestionsPhaseOptions,
): boolean {
  if (!options.flags.isEnabled("questions")) {
    // **Hard rule 7, as 17 October 2026 (a) reads it: nothing of a
    // false-flagged feature reaches the screen.** 17 October 2026 (b) section
    // 8 item 2 states it of this flag: "If it is false, nothing of the
    // questions phase is on screen."
    //
    // It used to append a paragraph saying the phase was switched off, which
    // was Finding C's answer when the question was how the trainee tells a
    // disabled feature from a broken one. That answer is now wrong: a
    // paragraph is something of the phase on screen. Nothing is rendered and
    // the caller is told nothing mounted, so the section stays hidden.
    return false;
  }

  const bank = buildQuestionBank(options.tree);
  // An unseeded run varies, so the bank does not become a memorised sequence.
  const random =
    options.seed === undefined ? Math.random : seededRandom(options.seed);
  const selection = selectQuestions(bank, random);
  const responses = new Map<string, QuestionResponse>();

  const status = element("p");
  status.dataset.role = "questions-status";
  if (selection.issue !== null) {
    status.textContent = `Only ${String(selection.questions.length)} of ${String(selection.requested)} questions are available from this document. The rest are not asked; nothing is invented to fill them.`;
    status.dataset.status = "error";
  } else {
    status.textContent = `${String(selection.questions.length)} questions. Answer, then reveal, then mark yourself.`;
    status.dataset.status = "ready";
  }
  target.append(status);
  options.onMoment?.("questions-started", {
    questions: selection.questions.length,
  });

  const save = (): void => {
    const stored = revealedWithoutAnswering(selection.questions, [
      ...responses.values(),
    ]);
    const result = options.storage.save(
      "questions",
      preflightStorageKey("questions", options.fileHash),
      { fileHash: options.fileHash, revealedWithoutAnswering: stored },
    );
    if (!result.ok) {
      status.textContent = result.error.message;
      status.dataset.status = "error";
    }
  };

  let finished = false;
  const list = element("ol");
  list.className = "questions-list";
  list.dataset.role = "questions-list";

  for (const question of selection.questions) {
    responses.set(question.id, {
      questionId: question.id,
      typed: "",
      revealed: false,
    });

    const entry = element("li");
    entry.dataset.questionId = question.id;
    entry.dataset.nodeId = question.nodeId;
    entry.append(element("p", question.prompt));

    const answerBox = element("textarea");
    answerBox.rows = 3;
    answerBox.dataset.role = "question-answer";
    answerBox.setAttribute("aria-label", question.prompt);
    answerBox.addEventListener("input", () => {
      const current = responses.get(question.id);
      /* v8 ignore next -- every question is seeded into the map above */
      if (current === undefined) return;
      responses.set(question.id, { ...current, typed: answerBox.value });
    });

    // The parsed value stays hidden until he reveals it.
    const revealed = element("p", question.answer);
    revealed.className = "question-revealed";
    revealed.dataset.role = "question-revealed";
    revealed.hidden = true;

    const reveal = element("button", "Reveal");
    reveal.type = "button";
    reveal.dataset.role = "question-reveal";
    reveal.addEventListener("click", () => {
      const current = responses.get(question.id);
      /* v8 ignore next -- every question is seeded into the map above */
      if (current === undefined) return;
      const next = { ...current, revealed: true };
      responses.set(question.id, next);
      revealed.hidden = false;
      reveal.disabled = true;
      entry.dataset.answered = String(isAnswered(next));
      save();
      // Ruling 84. Finished means every question revealed, which is the last
      // thing the phase does; it fires once, and reports two counts.
      const revealedCount = [...responses.values()].filter(
        ({ revealed: shown }) => shown,
      ).length;
      if (revealedCount === selection.questions.length && !finished) {
        finished = true;
        options.onMoment?.("questions-finished", {
          questions: selection.questions.length,
          revealed: revealedCount,
        });
      }
    });

    entry.append(answerBox, reveal, revealed);
    list.append(entry);
  }

  target.append(list);
  return true;
}
